import React, { useCallback, useState } from 'react';
import { ChevronDown, Eye, Trash2 } from 'react-feather';
import AccordionList from 'components/accordion-list';
import { ActiveStatus } from 'components/status';
import { H1 } from 'components/typography';
import { CreditCardBenefitsImg } from 'constants/explore-cc';
import { ICreditCard, ICreditCardState, IPolicy } from 'types';
import styles from './styles/creditCardList.scss';
import { Dropdown } from 'semantic-ui-react';
import { INSURANCE_TYPES_COLOR_MAP } from 'constants/insurance';
import PolicyListHeader from 'components/policy-list/PolicyListHeader';
import ConfirmAction from 'components/confirm-action';
import { getCCref } from 'helpers';

interface IProps {
  creditCard: ICreditCardState;
  isOwner: boolean;
  reference: ICreditCard[];
  selectPolicy(policy: IPolicy): void;
  deletePolicy(id: string, sk: string): void;
}

const CONTAINER_ID = 'CreditCards';

const getBenefits = (benefits = []) => {
  return benefits.map(({ name }) => {
    const SvgComponent =
      CreditCardBenefitsImg[name] || CreditCardBenefitsImg.default;
    return <SvgComponent key={name} className={styles.benefitImg} />;
  });
};

const AccordionTitle = ({
  index,
  isOwner,
  item,
  reference,
  deletePolicy,
  selectPolicy,
}) => {
  const creditCard = getCCref(item, reference);

  if (!creditCard) {
    return null;
  }

  const id = `${CONTAINER_ID}_${index}`;
  const handleSelect = () => selectPolicy(item);

  return (
    <div id={id} className={styles.title} onClick={handleSelect}>
      <div
        id={`${id}_Border`}
        className={styles.border}
        style={{ background: INSURANCE_TYPES_COLOR_MAP.creditCard }}
      />
      <div id={`${id}_Icon`} className={classnames(styles.col, styles.imgCol)}>
        <img alt={creditCard.name} src={`https:${creditCard.cardImageUrl}`} />
      </div>
      <div id={`${id}_Name`} className={classnames(styles.col, styles.flex2)}>
        <div className={styles.bold}>{creditCard.name}</div>
      </div>
      <div
        id={`${id}_Benefits`}
        className={classnames(styles.col, styles.benefitCol)}
      >
        <div className={styles.benefits}>
          {getBenefits(creditCard.benefits)}
        </div>
        <span className={styles.subtext}>
          See details <ChevronDown size={16} />
        </span>
      </div>
      <div
        id={`${id}_Status`}
        className={classnames(styles.col, styles.statusCol)}
      >
        <ActiveStatus textClassName={styles.status} text="Active" />
      </div>
      <DropdownComponent
        id={id}
        showDelete={isOwner}
        onSelect={handleSelect}
        onDelete={() => deletePolicy(item)}
      />
    </div>
  );
};

const AccordionContent = ({
  index,
  isOwner,
  item,
  reference,
  deletePolicy,
  selectPolicy,
}) => {
  const creditCard = getCCref(item, reference);

  if (!creditCard) {
    return null;
  }

  const id = `${CONTAINER_ID}_${index}`;
  const handleSelect = () => selectPolicy(item);

  return (
    <div id={`${id}_Content`} className={styles.content} onClick={handleSelect}>
      <DropdownComponent
        id={`${id}_Content`}
        showDelete={isOwner}
        onSelect={handleSelect}
        onDelete={() => deletePolicy(item)}
      />
      {creditCard.benefits.map(({ name }, idx) => {
        const SvgComponent =
          CreditCardBenefitsImg[name] || CreditCardBenefitsImg.default;
        return (
          <div
            id={`${id}_Benefits_${idx}`}
            className={styles.benefit}
            key={name}
          >
            <SvgComponent className={styles.benefitImg} />
            <b>{name}</b>
          </div>
        );
      })}
    </div>
  );
};

const DropdownComponent = ({ id, showDelete, onSelect, onDelete }) => (
  <div className={classnames(styles.col, styles.actionCol)}>
    <Dropdown
      id={`${id}_Menu`}
      icon="ellipsis vertical"
      floating={true}
      labeled={true}
      className={styles.action}
    >
      <Dropdown.Menu style={{ minHeight: showDelete ? 150 : 110 }}>
        <Dropdown.Header>View option</Dropdown.Header>
        <Dropdown.Item
          id={`${id}_View`}
          className={styles.actionItem}
          onClick={onSelect}
        >
          <Eye />
          View credit card
        </Dropdown.Item>
        {showDelete && (
          <Dropdown.Item
            id={`${id}_Delete`}
            className={styles.actionItem}
            onClick={onDelete}
          >
            <Trash2 />
            Delete
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

const CreditCardList = ({
  creditCard,
  isOwner,
  reference,
  deletePolicy,
  selectPolicy,
}: IProps) => {
  const { creditCards } = creditCard;
  const [deleteCC, setDeleteCC] = useState(null);

  const handleConfirmCancel = useCallback(() => setDeleteCC(null), []);

  const handleConfirm = useCallback(() => {
    deletePolicy(deleteCC.id, deleteCC.sk);
    setDeleteCC(null);
  }, [deletePolicy, deleteCC]);

  if (creditCards.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <H1 className={styles.h1}>Credit Cards</H1>
      <PolicyListHeader
        headerClassName={styles.header}
        headers={['', 'Type/name', 'Benefits', 'Status', '']}
      />
      <AccordionList
        id={CONTAINER_ID}
        className={styles.accordion}
        items={creditCards}
        componentProps={{
          isOwner,
          reference,
          deletePolicy: setDeleteCC,
          selectPolicy,
        }}
        titleComponent={AccordionTitle}
        contentComponent={AccordionContent}
        titleIcon="chevron down"
      />
      <ConfirmAction
        open={!!deleteCC}
        header="Delete credit card"
        content="Are you sure you want to delete this credit card?"
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default CreditCardList;
